import {
  toZonedTime,
  fromZonedTime,
  getTimezoneOffset as getTimezoneOffsetFns,
  formatInTimeZone as formatInTimeZoneFns,
  ToDateOptionsWithTZ,
  FormatOptionsWithTZ,
} from 'date-fns-tz'
import { getDateFnsLocale } from './locales'
import { getClientTimezone } from './timezones'

export const zonedTimeToUtc = (
  date: Date | string | number,
  timeZone: string,
  options?: ToDateOptionsWithTZ
) => {
  return fromZonedTime(date, timeZone, options)
}

export const convertTimezoneToLocal = (date: Date | number | string, timezone: string) => {
  return toZonedTime(date, timezone)
}

export const getTimezoneOffset = (timeZone: string, date?: Date | number) => {
  return getTimezoneOffsetFns(timeZone, date)
}

export const convertTimezone = (date: Date | number, timezone: string) => {
  return zonedTimeToUtc(date, timezone)
}

type FormatOptions = Omit<FormatOptionsWithTZ, 'locale'> & { locale?: string }

export const formatInTimeZone = (
  date: string | number | Date,
  timezone: string,
  format: string,
  options?: FormatOptions
) => {
  const dateFnsLocale = getDateFnsLocale(options?.locale)
  return formatInTimeZoneFns(date, timezone, format, { ...options, locale: dateFnsLocale })
}

/**
 * Parses and formats a datetime string in the client timezone, returning local date and time
 * strings, and a function to update the value in UTC ISO format.
 *
 * @example
 *   // Client timezone is "America/New_York"
 *   const { date, time, getUpdatedValue } = currentTimezoneDateTime('2024-06-01T15:30:00Z')
 *   // date: "2024-06-01"
 *   // time: "11:30:00"
 *   const newIso = getUpdatedValue({ date: '2024-06-02', time: '09:00:00' })
 *   // newIso: "2024-06-02T13:00:00.000Z"
 *
 * @param dateTimeString - ISO string of a datetime or undefined for no value
 * @returns An object with `date`, `time`, and `getUpdatedValue` for recomposing a new UTC ISO
 *   string.
 */
export const currentTimezoneDateTime = (dateTimeString: string | undefined) => {
  const clientTimezone = getClientTimezone()
  const parseStoredValue = (isoString: string | undefined) => {
    if (!isoString) {
      return { date: undefined, time: undefined }
    }

    const localDateStr = formatInTimeZone(isoString, clientTimezone, 'yyyy-MM-dd')
    const localTimeStr = formatInTimeZone(isoString, clientTimezone, 'HH:mm:ss')

    return { date: localDateStr, time: localTimeStr }
  }

  const { date, time } = parseStoredValue(dateTimeString)
  const defaultDate = formatInTimeZone(new Date(), clientTimezone, 'yyyy-MM-dd')
  const defaultTime = '00:00:00'

  const getUpdatedValue = ({ date, time }: { date?: string; time?: string }) => {
    const localDateTime = new Date(`${date ?? defaultDate}T${time ?? defaultTime}`)

    const utcIsoDateTime = formatInTimeZone(localDateTime, 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
    return utcIsoDateTime
  }

  return {
    date,
    time,
    getUpdatedValue,
  }
}
